import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedPage } from "../components/AnimatedPage";
import { CTASection } from "../components/CTASection";
import { FeatureCard } from "../components/FeatureCard";
import { ProjectImage } from "../components/ProjectImage";
import { Section } from "../components/Section";
import {
  differentiators,
  featuredProductNames,
  heroCapabilities,
  heroHeadline,
  heroSubheadline,
  logoPath,
  portfolio,
  process,
  products,
  services,
  spotlightMetrics,
  stats,
  subsidiariesStatement,
  techStack,
  trustBadges,
} from "../data/site";

export default function Home() {
  const featuredProducts = products.filter((product) =>
    featuredProductNames.includes(product.name),
  );

  return (
    <AnimatedPage>
      <Hero />
      <section className='border-y border-white/10 bg-white/[0.025] py-6'>
        <div className='container flex flex-col gap-4 text-sm text-white/60 md:flex-row md:items-center md:justify-between'>
          <p className='font-medium text-white/80'>
            Trusted by satisfied customers across ambitious digital projects.
          </p>
          <div className='flex flex-wrap gap-2'>
            {spotlightMetrics.map((metric) => (
              <span key={metric} className='chip'>
                {metric}
              </span>
            ))}
          </div>
        </div>
      </section>
      <Section
        eyebrow='What we build'
        title='Software services for modern businesses'
        description='Lazerwebs blends polished interfaces, dependable backend systems, and practical product strategy for organizations that need to move with confidence.'>
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
          {services.slice(0, 6).map((service, index) => (
            <FeatureCard key={service.title} {...service} index={index} />
          ))}
        </div>
      </Section>
      <Section
        eyebrow='Ready-made solutions'
        title='Launch faster with proven software products'
        description='Start from battle-tested business platforms, then customize workflows, integrations, branding, and growth features around your market.'
        className='bg-white/[0.025]'>
        <div className='grid gap-4 lg:grid-cols-3'>
          {featuredProducts.map((product, index) => {
            const Icon = product.icon;
            return (
              <motion.article
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
                className='glass-card p-6'>
                <div className='flex items-start justify-between gap-5'>
                  <div className='rounded-2xl bg-brand-gradient p-3 text-white shadow-glow'>
                    <Icon className='h-5 w-5' />
                  </div>
                  <span className='rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200'>
                    Demo-ready
                  </span>
                </div>
                <h3 className='mt-6 font-display text-xl font-semibold text-white'>
                  {product.name}
                </h3>
                <p className='mt-3 text-sm leading-6 text-white/60'>
                  {product.description}
                </p>
              </motion.article>
            );
          })}
        </div>
      </Section>
      <Section
        eyebrow='Why choose Lazerwebs'
        title='A premium partner for serious digital growth'
        description={subsidiariesStatement}>
        <div className='grid gap-4 md:grid-cols-2'>
          {differentiators.map((item, index) => (
            <FeatureCard key={item.title} {...item} index={index} />
          ))}
        </div>
      </Section>
      <Section
        eyebrow='Our process'
        title='From first conversation to lasting support'
        description='Clear steps, modern execution, and steady communication help your product move from vision to working software.'
        className='bg-white/[0.025]'>
        <div className='grid gap-4 lg:grid-cols-5'>
          {process.map((step, index) => (
            <motion.article
              key={step.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className='relative glass-card p-5'>
              <span className='text-sm font-semibold text-electric'>
                0{index + 1}
              </span>
              <h3 className='mt-5 font-display text-lg font-semibold text-white'>
                {step.title}
              </h3>
              <p className='mt-3 text-sm leading-6 text-white/60'>
                {step.description}
              </p>
            </motion.article>
          ))}
        </div>
      </Section>
      <Section
        eyebrow='Project showcase'
        title='Built for high-impact industries'
        description='From fintech to public-sector workflows, Lazerwebs designs software around the realities of each market.'>
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          {portfolio.slice(0, 4).map((project, index) => {
            return (
              <motion.article
                key={project.industry}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className='glass-card overflow-hidden'>
                <ProjectImage
                  src={project.image.src}
                  alt={project.image.alt}
                  industry={project.industry}
                  icon={project.icon}
                  priority={index === 0}
                  className='aspect-[16/10]'
                />
                <div className='p-5'>
                  <p className='text-xs font-semibold uppercase tracking-[0.2em] text-white/40'>
                    {project.industry}
                  </p>
                  <h3 className='mt-3 font-display text-lg font-semibold text-white'>
                    {project.title}
                  </h3>
                </div>
              </motion.article>
            );
          })}
        </div>
      </Section>
      <StatsAndStack />
      <CTASection />
    </AnimatedPage>
  );
}

function Hero() {
  return (
    <section className='relative min-h-[94vh] overflow-hidden pt-32'>
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_12%_15%,rgba(30,109,255,0.35),transparent_30%),radial-gradient(circle_at_82%_22%,rgba(74,24,255,0.35),transparent_32%),radial-gradient(circle_at_50%_86%,rgba(20,184,166,0.14),transparent_36%),linear-gradient(180deg,#0D1117_0%,#050505_88%)]' />
      <motion.div
        aria-hidden='true'
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        className='absolute right-[-140px] top-24 h-[440px] w-[440px] rounded-full border border-white/10 bg-[conic-gradient(from_180deg,rgba(30,109,255,0.35),rgba(74,24,255,0.12),rgba(20,184,166,0.18),rgba(30,109,255,0.35))] blur-sm'
      />
      <div className='container relative z-10 grid min-h-[calc(94vh-8rem)] items-center gap-12 pb-14 lg:grid-cols-[1.05fr_0.95fr]'>
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.72, ease: "easeOut" }}>
          <div className='inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm text-white/70 shadow-glass backdrop-blur-xl'>
            <img
              src={logoPath}
              alt=''
              className='h-6 w-6 rounded-lg object-cover'
            />
            <span>Subsidiary of Freakyplus Technologies Limited</span>
          </div>
          <h1 className='mt-7 max-w-5xl font-display text-5xl font-semibold leading-[1.03] text-white sm:text-6xl lg:text-7xl'>
            {heroHeadline}
          </h1>
          <p className='mt-6 max-w-2xl text-lg leading-8 text-white/70'>
            {heroSubheadline}
          </p>
          <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
            <Link to='/contact' className='btn btn-primary'>
              Start a Project
              <ArrowRight className='h-4 w-4' />
            </Link>
            <Link to='/products' className='btn btn-secondary'>
              View Our Solutions
            </Link>
          </div>
          <div className='mt-8 flex flex-wrap gap-2'>
            {trustBadges.map((badge) => (
              <span key={badge} className='chip'>
                <CheckCircle2 className='h-3.5 w-3.5 text-emerald-300' />
                {badge}
              </span>
            ))}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.12, ease: "easeOut" }}
          className='relative'>
          <div className='absolute -inset-8 rounded-[3rem] bg-brand-gradient opacity-20 blur-3xl' />
          <div className='relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] p-5 shadow-glass backdrop-blur-xl'>
            <div className='flex items-center justify-between border-b border-white/10 pb-5'>
              <div className='flex items-center gap-3'>
                <img
                  src={logoPath}
                  alt='Lazerwebs logo'
                  className='h-12 w-12 rounded-2xl object-cover'
                />
                <div>
                  <p className='font-display font-semibold text-white'>
                    Lazerwebs OS
                  </p>
                  <p className='text-xs text-white/50'>
                    Product delivery command center
                  </p>
                </div>
              </div>
              <span className='rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-xs font-semibold text-emerald-100'>
                Live
              </span>
            </div>
            <div className='grid gap-3 py-5 sm:grid-cols-5'>
              {heroCapabilities.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className='rounded-2xl border border-white/10 bg-ink/60 p-3 text-center'>
                    <Icon className='mx-auto h-5 w-5 text-electric' />
                    <p className='mt-2 text-xs font-medium text-white/70'>
                      {item.label}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className='grid gap-4 md:grid-cols-[1.2fr_0.8fr]'>
              <div className='rounded-3xl border border-white/10 bg-ink/60 p-5'>
                <div className='mb-5 flex items-center justify-between'>
                  <p className='text-sm font-semibold text-white'>
                    Delivery pipeline
                  </p>
                  <Sparkles className='h-4 w-4 text-cyan-300' />
                </div>
                {[
                  "Discovery",
                  "Design system",
                  "Engineering",
                  "Deployment",
                ].map((stage, index) => (
                  <div key={stage} className='mb-4 last:mb-0'>
                    <div className='mb-2 flex justify-between text-xs text-white/55'>
                      <span>{stage}</span>
                      <span>{72 + index * 7}%</span>
                    </div>
                    <div className='h-2 rounded-full bg-white/10'>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${72 + index * 7}%` }}
                        transition={{
                          duration: 1.1,
                          delay: 0.45 + index * 0.12,
                        }}
                        className='h-full rounded-full bg-brand-gradient'
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className='rounded-3xl border border-white/10 bg-ink/60 p-5'>
                <p className='text-sm font-semibold text-white'>
                  Client outcomes
                </p>
                <div className='mt-5 grid gap-3'>
                  {stats.slice(0, 3).map((stat) => (
                    <div key={stat.label}>
                      <p className='font-display text-2xl font-semibold text-white'>
                        {stat.value}
                      </p>
                      <p className='text-xs text-white/50'>{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StatsAndStack() {
  return (
    <Section
      eyebrow='Customer satisfaction'
      title='Built to feel fast, trustworthy, and enterprise-ready'
      description='Every solution is shaped around real business outcomes: automation, growth, stronger operations, and better customer experiences.'
      className='bg-white/[0.025]'>
      <div className='grid gap-5 lg:grid-cols-[0.9fr_1.1fr]'>
        <div className='grid gap-4 sm:grid-cols-2'>
          {stats.map((stat) => (
            <div key={stat.label} className='glass-card p-6'>
              <p className='font-display text-4xl font-semibold text-white'>
                {stat.value}
              </p>
              <p className='mt-3 text-sm text-white/60'>{stat.label}</p>
            </div>
          ))}
        </div>
        <div className='glass-card p-6 sm:p-8'>
          <p className='eyebrow'>Technology stack</p>
          <h3 className='mt-3 font-display text-2xl font-semibold text-white'>
            Modern tools for scalable digital products
          </h3>
          <div className='mt-7 flex flex-wrap gap-3'>
            {techStack.map((tech) => (
              <span
                key={tech}
                className='rounded-2xl border border-white/10 bg-white/[0.055] px-4 py-3 text-sm font-semibold text-white/70'>
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
